/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-08-06 08:34:34
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Field, getFormValues, change } from 'redux-form';
import {
  FormLayout,
  RangeSlider,
  SkeletonDisplayText,
  Link,
  Button,
  Stack,
  TextStyle
} from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderLabelTooltip } from 'src/components/views/common/forms/render-label-tooltip';
import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import {
  PRODUCT_ITEM_TYPE,
  PRODUCT_ITEM_LAYOUT,
  TEXT_ALIGNMENT,
  ASPECT_RATIO,
  PRODUCT_IMAGE_CROP
} from 'src/constants/theme-settings';
import { normalizeNumberRangeSlider } from 'src/helpers/form-validation-helper';
import { minValue, isRatio } from 'src/helpers/form-validation-helper';
import { URL } from 'src/constants/links';
import { buildOptionList } from 'src/helpers';

type Props = WithI18nProps;

type State = {
  showProductItemSection: boolean,
  showProductImageSection: boolean,
  showProductListSection: boolean,
  productItemType: Array<{ value: string, label: string }>,
  productItemLayout: Array<{ value: string, label: string }>,
  textAlignment: Array<{ value: string, label: string }>,
  aspectRatio: Array<{ value: string, label: string }>,
  productImgCrop: Array<{ value: string, label: string }>
};

class ListingTab extends Component<Props, State> {
  state = {
    showProductItemSection: true,
    showProductImageSection: true,
    showProductListSection: true,
    productItemType: [],
    productItemLayout: [],
    textAlignment: [],
    aspectRatio: [],
    productImgCrop: []
  };

  componentDidMount() {
    const { i18n } = this.props;

    const productItemType = buildOptionList(PRODUCT_ITEM_TYPE, i18n);
    const productItemLayout = buildOptionList(PRODUCT_ITEM_LAYOUT, i18n);
    const textAlignment = buildOptionList(TEXT_ALIGNMENT, i18n);
    const aspectRatio = buildOptionList(ASPECT_RATIO, i18n);
    const productImgCrop = buildOptionList(PRODUCT_IMAGE_CROP, i18n);
    this.setState({
      productItemType,
      productItemLayout,
      textAlignment,
      aspectRatio,
      productImgCrop
    });
  }

  componentWillReceiveProps(nextProps) {
    const { i18n } = nextProps;
    const { i18n: prevI18n } = this.props;

    if (i18n.locale !== prevI18n.locale) {
      const productItemType = buildOptionList(PRODUCT_ITEM_TYPE, i18n);
      const productItemLayout = buildOptionList(PRODUCT_ITEM_LAYOUT, i18n);
      const textAlignment = buildOptionList(TEXT_ALIGNMENT, i18n);
      const aspectRatio = buildOptionList(ASPECT_RATIO, i18n);
      const productImgCrop = buildOptionList(PRODUCT_IMAGE_CROP, i18n);
      this.setState({
        productItemType,
        productItemLayout,
        textAlignment,
        aspectRatio,
        productImgCrop
      });
    }
  }

  render() {
    const {
      i18n,
      updateField,
      loading,
      productsPerPage,
      productsPerRow,
      productsPerRowM,
      productItemType: productItemTypeValue,
      aspectRatio: aspectRatioValue
    } = this.props;
    const {
      showProductItemSection,
      showProductImageSection,
      showProductListSection,
      productItemType,
      productItemLayout,
      textAlignment,
      aspectRatio,
      productImgCrop
    } = this.state;

    return (
      <FormLayout>
        <TextStyle variation="subdued">
          {i18n.translate('AdminHTML.DefaultThemeSettings.ListingTab.helperText', {
            t1: (
              <Link external url={`${URL['DefaultThemeSettingsDoc']}#a2.1`}>
                {i18n.translate('AdminHTML.DefaultThemeSettings.ListingTab.helperText_t1')}
              </Link>
            )
          })}
        </TextStyle>
        {/* Product Item */}
        <div className="groupFields-label">
          <Button
            disclosure={showProductItemSection ? 'down' : 'up'}
            plain
            monochrome
            removeUnderline
            onClick={() => this.toggleState('showProductItemSection')}>
            <TextStyle variation="strong">
              {i18n.translate('AdminHTML.DefaultThemeSettings.ListingTab.gFProductItem_Label')}
            </TextStyle>
          </Button>
        </div>

        {showProductItemSection && (
          <div className="groupFields-content">
            <div style={{ marginTop: '-2rem' }}>
              <Stack vertical>
                <Field
                  name="products.productItemType"
                  component={RenderSelect}
                  label={i18n.translate('AdminHTML.DefaultThemeSettings.ListingTab.field1_Label')}
                  options={productItemType}
                />
                <Field
                  name="products.productItemLayout"
                  component={RenderSelect}
                  label={i18n.translate('AdminHTML.DefaultThemeSettings.ListingTab.field2_Label')}
                  options={productItemLayout}
                />
                <Field
                  name="products.textAlignment"
                  component={RenderSelect}
                  label={i18n.translate('AdminHTML.DefaultThemeSettings.ListingTab.field3_Label')}
                  options={textAlignment}
                  disabled={productItemTypeValue !== 'grid'}
                />
                <Field
                  name="products.showVendor"
                  component={RenderCheckbox}
                  label={i18n.translate('AdminHTML.DefaultThemeSettings.ListingTab.field4_Label')}
                />
                <Field
                  name="products.showPrice"
                  component={RenderCheckbox}
                  label={i18n.translate('AdminHTML.DefaultThemeSettings.ListingTab.field5_Label')}
                />
                <Field
                  name="products.showProductReview"
                  component={RenderCheckbox}
                  label={i18n.translate('AdminHTML.DefaultThemeSettings.ListingTab.field6_Label')}
                  tooltip={i18n.translate(
                    'AdminHTML.DefaultThemeSettings.ListingTab.field6_Tooltip'
                  )}
                />
                <Field
                  name="products.showDetailWhenHover"
                  component={RenderCheckbox}
                  label={i18n.translate('AdminHTML.DefaultThemeSettings.ListingTab.field7_Label')}
                  tooltip={i18n.translate(
                    'AdminHTML.DefaultThemeSettings.ListingTab.field7_Tooltip'
                  )}
                />
                <Field
                  name="products.activeImageSwap"
                  component={RenderCheckbox}
                  label={i18n.translate('AdminHTML.DefaultThemeSettings.ListingTab.field8_Label')}
                />
              </Stack>
            </div>
          </div>
        )}

        <div className="breakLine" />

        {/* Product image */}
        <div className="groupFields-label">
          <Button
            disclosure={showProductImageSection ? 'down' : 'up'}
            plain
            monochrome
            removeUnderline
            onClick={() => this.toggleState('showProductImageSection')}>
            <TextStyle variation="strong">
              {i18n.translate('AdminHTML.DefaultThemeSettings.ListingTab.gFProductImage_Label')}
            </TextStyle>
          </Button>
        </div>

        {showProductImageSection && (
          <div className="groupFields-content">
            <div style={{ marginTop: '-2rem' }}>
              <Stack vertical>
                <Field
                  name="products.aspectRatio"
                  component={RenderSelect}
                  label={i18n.translate('AdminHTML.DefaultThemeSettings.ListingTab.field9_Label')}
                  options={aspectRatio}
                  helperText={i18n.translate(
                    'AdminHTML.DefaultThemeSettings.ListingTab.field9_HelperText',
                    {
                      t1: (
                        <Link external url={URL['DefaultThemeSettingsAspectRatio']}>
                          {i18n.translate(
                            'AdminHTML.DefaultThemeSettings.ListingTab.field9_HelperText_t1'
                          )}
                        </Link>
                      )
                    }
                  )}
                />
                <Field
                  name="products.aspectRatioOther"
                  component={RenderTextField}
                  label={i18n.translate('AdminHTML.DefaultThemeSettings.ListingTab.field10_Label')}
                  placeholder={i18n.translate(
                    'AdminHTML.DefaultThemeSettings.ListingTab.field10_Placeholder'
                  )}
                  validate={[isRatio]}
                  anotherProps={{
                    disabled: aspectRatioValue !== 'other'
                  }}
                />
                <Field
                  name="products.productImgCrop"
                  component={RenderSelect}
                  label={i18n.translate('AdminHTML.DefaultThemeSettings.ListingTab.field11_Label')}
                  options={productImgCrop}
                />
              </Stack>
            </div>
          </div>
        )}

        <div className="breakLine" />

        {/* Product list */}
        <div className="groupFields-label">
          <Button
            disclosure={showProductListSection ? 'down' : 'up'}
            plain
            monochrome
            removeUnderline
            onClick={() => this.toggleState('showProductListSection')}>
            <RenderLabelTooltip
              label={
                <TextStyle variation="strong">
                  {i18n.translate('AdminHTML.DefaultThemeSettings.ListingTab.gFProductList_Label')}
                </TextStyle>
              }
              tooltip={i18n.translate(
                'AdminHTML.DefaultThemeSettings.ListingTab.gFProductList_Tooltip'
              )}
            />
          </Button>
        </div>

        {showProductListSection && (
          <div className="groupFields-content">
            <div style={{ marginTop: '-2rem' }}>
              <Stack vertical>
                <div>
                  <p className="Polaris-Label">
                    <label style={{ display: 'inline' }} className="Polaris-Label__Text">
                      {i18n.translate('AdminHTML.DefaultThemeSettings.ListingTab.field12_Label')}
                    </label>
                  </p>
                  {loading ? (
                    <div style={{ marginTop: '1rem' }}>
                      <SkeletonDisplayText size="small" />
                    </div>
                  ) : (
                    <div className="range-slider-input">
                      <Field
                        name="products.productsPerPage"
                        component={RenderTextField}
                        onBlur={(event, value) =>
                          this.onBlurTextField(event, value, 'productsPerPage')
                        }
                        normalize={(value, prevValue) =>
                          normalizeNumberRangeSlider(value, prevValue, 50)
                        }
                        validate={[minValue(2)]}
                        anotherProps={{
                          connectedLeft: (
                            <RangeSlider
                              value={productsPerPage}
                              onChange={value => updateField('products.productsPerPage', value)}
                              max={50}
                              min={2}
                            />
                          )
                        }}
                      />
                    </div>
                  )}
                </div>
                <div>
                  <p className="Polaris-Label">
                    <label style={{ display: 'inline' }} className="Polaris-Label__Text">
                      {i18n.translate('AdminHTML.DefaultThemeSettings.ListingTab.field13_Label')}
                    </label>
                  </p>
                  {loading ? (
                    <div style={{ marginTop: '1rem' }}>
                      <SkeletonDisplayText size="small" />
                    </div>
                  ) : (
                    <div className="range-slider-input">
                      <Field
                        name="products.productsPerRow"
                        component={RenderTextField}
                        onBlur={(event, value) =>
                          this.onBlurTextField(event, value, 'productsPerRow')
                        }
                        normalize={(value, prevValue) =>
                          normalizeNumberRangeSlider(value, prevValue, 6)
                        }
                        validate={[minValue(2)]}
                        anotherProps={{
                          connectedLeft: (
                            <RangeSlider
                              value={productsPerRow}
                              onChange={value => updateField('products.productsPerRow', value)}
                              max={6}
                              min={2}
                            />
                          )
                        }}
                      />
                    </div>
                  )}
                </div>
                <div>
                  <p className="Polaris-Label">
                    <label style={{ display: 'inline' }} className="Polaris-Label__Text">
                      {i18n.translate('AdminHTML.DefaultThemeSettings.ListingTab.field14_Label')}
                    </label>
                  </p>
                  {loading ? (
                    <div style={{ marginTop: '1rem' }}>
                      <SkeletonDisplayText size="small" />
                    </div>
                  ) : (
                    <div className="range-slider-input">
                      <Field
                        name="products.productsPerRowM"
                        component={RenderTextField}
                        onBlur={(event, value) =>
                          this.onBlurTextField(event, value, 'productsPerRowM')
                        }
                        normalize={(value, prevValue) =>
                          normalizeNumberRangeSlider(value, prevValue, 2)
                        }
                        validate={[minValue(1)]}
                        anotherProps={{
                          connectedLeft: (
                            <RangeSlider
                              value={productsPerRowM}
                              onChange={value => updateField('products.productsPerRowM', value)}
                              max={2}
                              min={1}
                            />
                          )
                        }}
                      />
                    </div>
                  )}
                </div>
              </Stack>
            </div>
          </div>
        )}
      </FormLayout>
    );
  }

  toggleState = (fieldName: string) => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  // Handle blur input
  onBlurTextField = (event, value, name) => {
    const { updateField } = this.props;
    if (!value) {
      event.preventDefault();
      updateField(name, '0');
    }
  };
}

const mapStateToProps = (state, props) => {
  const defaultThemeSettingsForm = getFormValues('DefaultThemeSettingsForm')(state);
  const { defaultThemeSettings } = state;

  return {
    loading: _.get(defaultThemeSettings, 'loadingDefaultThemeSettings', false),
    productsPerPage: _.get(defaultThemeSettingsForm, 'products.productsPerPage', 0),
    productsPerRow: _.get(defaultThemeSettingsForm, 'products.productsPerRow', 0),
    productsPerRowM: _.get(defaultThemeSettingsForm, 'products.productsPerRowM', 0),
    productItemType: _.get(defaultThemeSettingsForm, 'products.productItemType', 'grid'),
    aspectRatio: _.get(defaultThemeSettingsForm, 'products.aspectRatio', 'auto')
  };
};

const mapDispatchToProps = dispatch => ({
  updateField: (id, data) => dispatch(change(`DefaultThemeSettingsForm`, id, data))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(ListingTab));
